import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Loading from '@/components/Loading'
import { usePageTitle } from '@/utils/hooks/usePageTitle'
import { getSurveyState, submitSurvey, SurveySubmission } from '@/api/surveyAPI'

const SUPPORT_EMAIL = 'help@fundsorter.com'
const NAVY = '#1C3045'
// Shown as a counter so the limit is visible.
const MAX_TEXT = 5000

type View = 'loading' | 'open' | 'submitted' | 'notfound' | 'error'

const homeButtonSx = {
  mt: 6,
  backgroundColor: NAVY,
  '&:hover': { backgroundColor: '#16283a' },
  px: 4,
  py: 1.5,
  textTransform: 'none',
  fontSize: '16px',
  borderRadius: 2,
} as const

export default function SurveyPage() {
  usePageTitle('Survey')
  const { uuid } = useParams<{ uuid: string }>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [view, setView] = useState<View>('loading')
  const [likes, setLikes] = useState('')
  const [improvements, setImprovements] = useState('')
  const [receivedFunding, setReceivedFunding] = useState<boolean | null>(null)
  const [receivedFundingDetails, setReceivedFundingDetails] = useState('')
  const [rating, setRating] = useState<number | null>(null)
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [contactOptIn, setContactOptIn] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const fundingRef = useRef<HTMLDivElement>(null)
  const ratingRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!uuid) {
      setView('notfound')
      return
    }
    getSurveyState(uuid)
      .then((res) => setView(res === 'notfound' ? 'notfound' : res.status))
      .catch(() => setView('error'))
  }, [uuid])

  const fundingMissing = receivedFunding === null
  const ratingMissing = rating === null

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setAttemptedSubmit(true)
    setSubmitError(null)

    // Required fields: received funding + rating. Move focus to the first
    // invalid control so keyboard/screen-reader users land on the problem.
    if (fundingMissing) {
      fundingRef.current?.querySelector('button')?.focus()
      return
    }
    if (ratingMissing) {
      ratingRef.current?.querySelector('button')?.focus()
      return
    }
    if (!uuid) return

    setSubmitting(true)
    const submission: SurveySubmission = {
      likes: likes.trim() || undefined,
      improvements: improvements.trim() || undefined,
      received_funding: receivedFunding,
      received_funding_details: receivedFundingDetails.trim() || undefined,
      rating,
      is_anonymous: isAnonymous,
      // Contact opt-in only applies when not anonymous.
      contact_opt_in: isAnonymous ? false : contactOptIn,
    }
    try {
      const result = await submitSurvey(uuid, submission)
      // A 404 means the link is no longer valid — there's nothing to keep, so
      // swap to that view. Transient errors keep the form (handled below).
      setView(result === 'notfound' ? 'notfound' : 'submitted')
    } catch {
      setSubmitError(
        'Sorry, something went wrong submitting your feedback. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const container = (children: React.ReactNode) => (
    <Box
      component="main"
      sx={{
        maxWidth: 640,
        width: '100%',
        mx: 'auto',
        px: isMobile ? 2 : 3,
        py: 5,
        color: NAVY,
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <img src="/Fundsorter-h-black800px.png" alt="Fundsorter" width={300} />
      </Box>
      {children}
    </Box>
  )

  const homeButton = (
    <Button
      component={Link}
      to="/"
      variant="contained"
      disableRipple
      sx={homeButtonSx}
    >
      Go to Fundsorter
    </Button>
  )

  if (view === 'loading') return <Loading />

  if (view === 'notfound') {
    return container(
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          This survey link isn't valid
        </Typography>
        <Typography>
          The link may be incorrect or expired. If you'd like to share feedback,
          email us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </Typography>
        {homeButton}
      </Box>,
    )
  }

  if (view === 'error') {
    return container(
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          Something went wrong
        </Typography>
        <Typography>
          Please try again in a moment, or email us at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </Typography>
        {homeButton}
      </Box>,
    )
  }

  if (view === 'submitted') {
    return container(
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          Thanks for your feedback!
        </Typography>
        <Typography>
          We really appreciate you taking the time. If you've got anything
          further to add, contact us at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </Typography>
        {homeButton}
      </Box>,
    )
  }

  // view === 'open'
  const textFieldSx = {
    mb: 3,
    '& .MuiOutlinedInput-input': {
      backgroundColor: '#fff',
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
    },
    '& .MuiFormHelperText-root': { textAlign: 'right', mr: 0 },
  }
  const labelSx = { fontWeight: 600, mb: 1, display: 'block' }
  // Navy/white styling for the selected toggle, matching the brand buttons.
  const toggleSelectedSx = {
    '& .MuiToggleButton-root.Mui-selected': {
      backgroundColor: NAVY,
      color: '#fff',
      '&:hover': { backgroundColor: '#16283a' },
    },
  }
  const required = (
    <Box component="span" aria-hidden="true" sx={{ color: '#c0392b', ml: 0.5 }}>
      *
    </Box>
  )

  return container(
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mb: 10 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        How are you finding Fundsorter?
      </Typography>
      <Typography sx={{ mb: 4 }}>
        It's been around three months since you signed up, and we'd love to hear
        how things are going. Your feedback genuinely helps shape what we build
        next.
      </Typography>

      <Typography component="label" htmlFor="survey-likes" sx={labelSx}>
        What do you like about Fundsorter?
      </Typography>
      <TextField
        id="survey-likes"
        value={likes}
        onChange={(e) => setLikes(e.target.value)}
        multiline
        minRows={3}
        maxRows={15}
        fullWidth
        slotProps={{ htmlInput: { maxLength: MAX_TEXT } }}
        helperText={`${likes.length.toLocaleString()} / ${MAX_TEXT.toLocaleString()} characters`}
        sx={textFieldSx}
      />

      <Typography component="label" htmlFor="survey-improvements" sx={labelSx}>
        What could we improve?
      </Typography>
      <TextField
        id="survey-improvements"
        value={improvements}
        onChange={(e) => setImprovements(e.target.value)}
        multiline
        minRows={3}
        maxRows={15}
        fullWidth
        slotProps={{ htmlInput: { maxLength: MAX_TEXT } }}
        helperText={`${improvements.length.toLocaleString()} / ${MAX_TEXT.toLocaleString()} characters`}
        sx={textFieldSx}
      />

      <Typography id="survey-funding-label" sx={labelSx}>
        Have you received funding through Fundsorter?{required}
      </Typography>
      <ToggleButtonGroup
        ref={fundingRef}
        value={receivedFunding}
        exclusive
        onChange={(_, v) => v !== null && setReceivedFunding(v)}
        aria-labelledby="survey-funding-label"
        aria-describedby={
          attemptedSubmit && fundingMissing ? 'survey-funding-error' : undefined
        }
        sx={{
          mb: attemptedSubmit && fundingMissing ? 1 : 3,
          '& .MuiToggleButton-root': { width: 100, textTransform: 'none' },
          ...toggleSelectedSx,
        }}
      >
        <ToggleButton value={true}>Yes</ToggleButton>
        <ToggleButton value={false}>No</ToggleButton>
      </ToggleButtonGroup>
      {attemptedSubmit && fundingMissing && (
        <Typography
          id="survey-funding-error"
          role="alert"
          sx={{ color: '#c0392b', mb: 3, fontSize: 14 }}
        >
          Please choose Yes or No.
        </Typography>
      )}

      <Typography component="label" htmlFor="survey-funding-details" sx={labelSx}>
        Anything you want to tell us about receiving funding?
      </Typography>
      <TextField
        id="survey-funding-details"
        value={receivedFundingDetails}
        onChange={(e) => setReceivedFundingDetails(e.target.value)}
        multiline
        minRows={3}
        maxRows={15}
        fullWidth
        slotProps={{ htmlInput: { maxLength: MAX_TEXT } }}
        helperText={`${receivedFundingDetails.length.toLocaleString()} / ${MAX_TEXT.toLocaleString()} characters`}
        sx={textFieldSx}
      />

      <Typography id="survey-rating-label" sx={labelSx}>
        How likely are you to recommend Fundsorter? (0–10){required}
      </Typography>
      <ToggleButtonGroup
        ref={ratingRef}
        value={rating}
        exclusive
        onChange={(_, v) => v !== null && setRating(v)}
        aria-labelledby="survey-rating-label"
        aria-describedby={
          attemptedSubmit && ratingMissing ? 'survey-rating-error' : undefined
        }
        sx={{
          mb: attemptedSubmit && ratingMissing ? 1 : 3,
          flexWrap: 'wrap',
          '& .MuiToggleButton-root': { width: 48 },
          ...toggleSelectedSx,
        }}
      >
        {Array.from({ length: 11 }, (_, i) => (
          <ToggleButton key={i} value={i} aria-label={`${i} out of 10`}>
            {i}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {attemptedSubmit && ratingMissing && (
        <Typography
          id="survey-rating-error"
          role="alert"
          sx={{ color: '#c0392b', mb: 3, fontSize: 14 }}
        >
          Please choose a rating from 0 to 10.
        </Typography>
      )}

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
            />
          }
          label="Keep my response anonymous"
        />
      </Box>

      {!isAnonymous && (
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={contactOptIn}
                onChange={(e) => setContactOptIn(e.target.checked)}
              />
            }
            label="I'm happy to be contacted about my feedback"
          />
        </Box>
      )}

      <Typography sx={{ mb: 2, fontSize: 14 }}>
        Fields marked{' '}
        <Box component="span" sx={{ color: '#c0392b' }}>
          *
        </Box>{' '}
        are required.
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        disableRipple
        disabled={submitting}
        sx={{
          backgroundColor: NAVY,
          '&:hover': { backgroundColor: '#16283a' },
          px: 4,
          py: 1.5,
          textTransform: 'none',
          fontSize: 16,
        }}
      >
        {submitting ? 'Submitting…' : 'Submit feedback'}
      </Button>
    </Box>,
  )
}
